"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCurrency } from "@/utils/pricing";
import {
    calculatePaymentEstimate,
    PAYMENT_CREDIT_PROFILES,
    PAYMENT_LOAN_TERMS,
    PAYMENT_PREFERENCES_STORAGE_KEY,
    type PaymentCreditProfileId,
    type PaymentLoanTerm
} from "@/utils/paymentEstimate";
import styles from "../VehicleDetailsPage.module.css";

const MAX_DOWN_PAYMENT = 15_000;
const PRICING_EVENT = "thiscar:open-vdp-pricing";
const CHECK_AVAILABILITY_BUTTON_ID = "vdp-check-availability";
const GET_PRE_APPROVED_BUTTON_ID = "vdp-get-pre-approved";

type PricingView = "finance" | "cash";

interface StoredPaymentPreferences {
    down?: number;
    term?: number;
    tier?: string;
    customized?: boolean;
}

interface VehiclePricingProps {
    advertisedPrice: number;
}

const formatWholeCurrency = (value: number) => formatCurrency(Math.round(value));

const isCreditProfileId = (value: string): value is PaymentCreditProfileId => value in PAYMENT_CREDIT_PROFILES;

const isLoanTerm = (value: number): value is PaymentLoanTerm => PAYMENT_LOAN_TERMS.some((term) => term === value);

export default function VehiclePricing({ advertisedPrice }: VehiclePricingProps) {
    const defaultDownPayment = Math.round(advertisedPrice * 0.1);
    const [downPayment, setDownPayment] = useState(defaultDownPayment);
    const [termMonths, setTermMonths] = useState<PaymentLoanTerm>(72);
    const [creditProfileId, setCreditProfileId] = useState<PaymentCreditProfileId>("excellent");
    const [isCustomized, setIsCustomized] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<PricingView>("finance");
    const pricingDialogRef = useRef<HTMLElement>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        try {
            const preferences = JSON.parse(
                window.localStorage.getItem(PAYMENT_PREFERENCES_STORAGE_KEY) || "null"
            ) as StoredPaymentPreferences | null;

            if (!preferences?.customized) return;

            if (typeof preferences.down === "number" && Number.isFinite(preferences.down)) {
                setDownPayment(Math.min(MAX_DOWN_PAYMENT, Math.max(0, preferences.down)));
            }
            if (typeof preferences.term === "number" && isLoanTerm(preferences.term)) {
                setTermMonths(preferences.term);
            }
            if (typeof preferences.tier === "string" && isCreditProfileId(preferences.tier)) {
                setCreditProfileId(preferences.tier);
            }
            setIsCustomized(true);
        } catch {
            // Storage can be unavailable in privacy modes; the estimator still works in memory.
        }
    }, []);

    useEffect(() => {
        const handlePricingEvent = (event: Event) => {
            const pricingEvent = event as CustomEvent<{ view?: PricingView }>;
            setView(pricingEvent.detail?.view === "cash" ? "cash" : "finance");
            returnFocusRef.current = document.activeElement as HTMLElement | null;
            setIsOpen(true);
        };

        window.addEventListener(PRICING_EVENT, handlePricingEvent);
        return () => window.removeEventListener(PRICING_EVENT, handlePricingEvent);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.requestAnimationFrame(() => pricingDialogRef.current?.focus());

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
            window.requestAnimationFrame(() => returnFocusRef.current?.focus());
        };
    }, [isOpen]);

    const estimate = useMemo(
        () =>
            calculatePaymentEstimate({
                advertisedPrice,
                downPayment,
                termMonths,
                creditProfileId
            }),
        [advertisedPrice, creditProfileId, downPayment, termMonths]
    );

    const persistPreferences = useCallback((down: number, term: PaymentLoanTerm, tier: PaymentCreditProfileId) => {
        setIsCustomized(true);
        try {
            window.localStorage.setItem(
                PAYMENT_PREFERENCES_STORAGE_KEY,
                JSON.stringify({ down, term, tier, customized: true })
            );
        } catch {
            // Keep the current-session selections if storage is unavailable.
        }
    }, []);

    const updateDownPayment = (value: number) => {
        const nextValue = Math.min(MAX_DOWN_PAYMENT, Math.max(0, value));
        setDownPayment(nextValue);
        persistPreferences(nextValue, termMonths, creditProfileId);
    };

    const updateTerm = (term: PaymentLoanTerm) => {
        setTermMonths(term);
        persistPreferences(downPayment, term, creditProfileId);
    };

    const updateCredit = (tier: PaymentCreditProfileId) => {
        setCreditProfileId(tier);
        persistPreferences(downPayment, termMonths, tier);
    };

    const openPricing = (nextView: PricingView) => {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setView(nextView);
        setIsOpen(true);
    };

    const closePricing = () => setIsOpen(false);

    const runVehicleAction = (buttonId: string) => {
        setIsOpen(false);
        if (buttonId === GET_PRE_APPROVED_BUTTON_ID) {
            // Keep the new-tab navigation directly within the user's gesture.
            document.getElementById(buttonId)?.click();
            return;
        }
        window.setTimeout(() => document.getElementById(buttonId)?.click(), 0);
    };

    if (!estimate) return null;

    const selectedCredit = PAYMENT_CREDIT_PROFILES[creditProfileId];
    const range = `${formatWholeCurrency(estimate.minimumMonthlyPayment)}–${formatWholeCurrency(estimate.maximumMonthlyPayment)}`;

    return (
        <>
            <div
                className={styles["summaryGrid"]}
                aria-label="Payment and price">
                <button
                    type="button"
                    className={styles["summaryButton"]}
                    onClick={() => openPricing("finance")}>
                    <small className={styles["summaryLabel"]}>EST. PAYMENT RANGE</small>
                    <strong className={styles["summaryValue"]}>
                        {range}
                        <span>/mo</span>
                    </strong>
                    <em className={styles["summaryLink"]}>Adjust payment →</em>
                </button>
                <button
                    type="button"
                    className={styles["summaryButton"]}
                    onClick={() => openPricing("cash")}>
                    <small className={styles["summaryLabel"]}>PRICE</small>
                    <strong className={styles["summaryValue"]}>{formatWholeCurrency(advertisedPrice)}</strong>
                    <em className={styles["summaryLink"]}>See price details →</em>
                </button>
            </div>
            <p className={styles["estimateNote"]}>
                {isCustomized
                    ? `Estimated payment based on your selections: ${formatWholeCurrency(downPayment)} down, ${termMonths} months and ${creditProfileId} credit.`
                    : `Estimate based on 10% down, ${termMonths} months and ${creditProfileId} credit.`}
            </p>

            {isOpen &&
                createPortal(
                    <div
                        className={styles["pricingOverlay"]}
                        onClick={closePricing}>
                        <section
                            ref={pricingDialogRef}
                            className={styles["pricingModal"]}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="pricing-title"
                            tabIndex={-1}
                            onClick={(event) => event.stopPropagation()}>
                            <button
                                type="button"
                                className={styles["pricingClose"]}
                                onClick={closePricing}
                                aria-label="Close pricing">
                                ×
                            </button>

                            <header className={styles["pricingModalHead"]}>
                                <p>STRAIGHTFORWARD PRICING</p>
                                <h2 id="pricing-title">Choose what works for you.</h2>
                                <span>One honest price. No dealer fees. No surprises at the finish line.</span>
                            </header>

                            <div className={styles["pricingOptions"]}>
                                <button
                                    type="button"
                                    className={view === "finance" ? styles["pricingOptionActive"] : undefined}
                                    onClick={() => setView("finance")}>
                                    <small>EST. PAYMENT RANGE</small>
                                    <strong>{range}/mo</strong>
                                    <span>Adjust your estimate</span>
                                </button>
                                <button
                                    type="button"
                                    className={view === "cash" ? styles["pricingOptionActive"] : undefined}
                                    onClick={() => setView("cash")}>
                                    <small>PRICE</small>
                                    <strong>{formatWholeCurrency(advertisedPrice)}</strong>
                                    <span>See price details</span>
                                </button>
                            </div>

                            {view === "finance" ? (
                                <div className={styles["pricingFinance"]}>
                                    <div className={styles["pricingLive"]}>
                                        <small>YOUR ESTIMATED PAYMENT RANGE</small>
                                        <strong>
                                            {range}
                                            <span>/month</span>
                                        </strong>
                                    </div>

                                    <label className={styles["pricingDownPayment"]}>
                                        Down payment
                                        <span className={styles["pricingFieldValue"]}>
                                            $
                                            <input
                                                aria-label="Down payment amount"
                                                type="number"
                                                min="0"
                                                max={MAX_DOWN_PAYMENT}
                                                step="100"
                                                value={downPayment}
                                                onChange={(event) => {
                                                    if (event.target.value === "") return;
                                                    updateDownPayment(Number(event.target.value));
                                                }}
                                            />
                                        </span>
                                        <input
                                            aria-label="Down payment slider"
                                            type="range"
                                            min="0"
                                            max={MAX_DOWN_PAYMENT}
                                            step="100"
                                            value={downPayment}
                                            onChange={(event) => updateDownPayment(Number(event.target.value))}
                                        />
                                    </label>

                                    <fieldset className={styles["pricingCredit"]}>
                                        <legend>About where is your credit score?</legend>
                                        <div className={styles["paymentCreditGrid"]}>
                                            {Object.entries(PAYMENT_CREDIT_PROFILES).map(([id, profile]) => {
                                                const tier = id as PaymentCreditProfileId;
                                                const active = creditProfileId === tier;
                                                return (
                                                    <button
                                                        key={tier}
                                                        type="button"
                                                        className={active ? styles["pricingChoiceActive"] : undefined}
                                                        aria-pressed={active}
                                                        onClick={() => updateCredit(tier)}>
                                                        <strong>{profile.label}</strong>
                                                        <span>{profile.range}</span>
                                                        <small>{profile.description}</small>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </fieldset>

                                    <fieldset className={styles["pricingTerm"]}>
                                        <legend>
                                            Loan term <strong>{termMonths} months</strong>
                                        </legend>
                                        <div>
                                            {PAYMENT_LOAN_TERMS.map((term) => (
                                                <button
                                                    key={term}
                                                    type="button"
                                                    className={
                                                        termMonths === term ? styles["pricingChoiceActive"] : undefined
                                                    }
                                                    aria-pressed={termMonths === term}
                                                    onClick={() => updateTerm(term)}>
                                                    {term}
                                                </button>
                                            ))}
                                        </div>
                                    </fieldset>

                                    <div className={styles["pricingBreakdown"]}>
                                        <p>
                                            <span>Vehicle price</span>
                                            <strong>{formatWholeCurrency(advertisedPrice)}</strong>
                                        </p>
                                        <p>
                                            <span>Down payment</span>
                                            <strong>{formatWholeCurrency(downPayment)}</strong>
                                        </p>
                                        <p>
                                            <span>Dealer fees</span>
                                            <strong>$0</strong>
                                        </p>
                                        <p>
                                            <span>Credit range</span>
                                            <strong>
                                                {selectedCredit.label} · {selectedCredit.range}
                                            </strong>
                                        </p>
                                        <p>
                                            <span>Loan term</span>
                                            <strong>{termMonths} months</strong>
                                        </p>
                                        <p className={styles["pricingTotal"]}>
                                            <span>Estimated payment range</span>
                                            <strong>{range}/mo</strong>
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        className={styles["pricingPrimary"]}
                                        onClick={() => runVehicleAction(GET_PRE_APPROVED_BUTTON_ID)}>
                                        Get Financed
                                    </button>
                                    <small className={styles["pricingDisclaimer"]}>
                                        We use a fixed sample rate for each credit range to
                                        calculate this estimate. This is not a credit decision or financing offer. Your
                                        approved payment may be higher or lower. Tax, title and registration are
                                        additional.
                                    </small>
                                </div>
                            ) : (
                                <div>
                                    <div className={styles["pricingBreakdown"]}>
                                        <p>
                                            <span>Vehicle price</span>
                                            <strong>{formatWholeCurrency(advertisedPrice)}</strong>
                                        </p>
                                        <p>
                                            <span>Dealer fees</span>
                                            <strong>$0</strong>
                                        </p>
                                        <p className={styles["pricingTotal"]}>
                                            <span>THIScar No-Haggle Price*</span>
                                            <strong>{formatWholeCurrency(advertisedPrice)}</strong>
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles["pricingPrimary"]}
                                        onClick={() => runVehicleAction(CHECK_AVAILABILITY_BUTTON_ID)}>
                                        Check Availability
                                    </button>
                                    <small className={styles["pricingDisclaimer"]}>
                                        * Government-imposed charges are additional and vary by transaction and
                                        jurisdiction.
                                    </small>
                                </div>
                            )}
                        </section>
                    </div>,
                    document.body
                )}
        </>
    );
}

export function openVehiclePricing(view: PricingView = "finance") {
    window.dispatchEvent(new CustomEvent(PRICING_EVENT, { detail: { view } }));
}
